import { getApiUrl } from './apiConfig';

/**
 * Parse Tours count from legacy session notes (no exercise rows).
 * Keep in sync with backend/utils/trainingSessionExerciseHydration.js
 */
export function parseToursCountFromSessionNotes(notes) {
  if (notes == null || notes === '') return null;
  const text = String(notes).trim();
  if (!text) return null;

  const toCount = (raw) => {
    const n = parseInt(raw, 10);
    return Number.isNaN(n) || n < 0 ? null : n;
  };

  const lineMatch = text.match(/^\s*Tours:\s*(\d+)\s*$/im);
  if (lineMatch) return toCount(lineMatch[1]);

  const countFirst = text.match(/^\s*(\d+)\s*(?:x\s*)?tours?\.?\b/im);
  if (countFirst) return toCount(countFirst[1]);

  const toursThenCount = text.match(/\btours?\s*:?\s*(\d+)\b/i);
  if (toursThenCount) return toCount(toursThenCount[1]);

  const countBeforeTours = text.match(/\b(\d+)\s*(?:x\s*)?tours?\.?\b/i);
  if (countBeforeTours) return toCount(countBeforeTours[1]);

  return null;
}

function rowsIncludeToursExercise(rows) {
  return rows.some((row) => {
    const type = String(
      row.exercise_type || row.catalog_exercise_type_name || ''
    ).toLowerCase();
    const name = String(row.exercise_name || '').toLowerCase();
    return type === 'tours' || name.includes('tour');
  });
}

let cachedToursCatalog = null;

export async function fetchToursCatalogExercise() {
  if (cachedToursCatalog) return cachedToursCatalog;

  const typesRes = await fetch(`${getApiUrl()}/api/exercise-types`);
  if (!typesRes.ok) return null;
  const types = await typesRes.json();
  const toursType = (Array.isArray(types) ? types : []).find(
    (t) => String(t.exercise_type_name || '').toLowerCase() === 'tours'
  );
  if (!toursType?.id) return null;

  const exRes = await fetch(
    `${getApiUrl()}/api/exercises?exercise_type_id=${encodeURIComponent(String(toursType.id))}`
  );
  if (!exRes.ok) return null;
  const exercises = await exRes.json();
  const list = Array.isArray(exercises) ? exercises : [];
  const pick =
    list.find((e) => /tour/i.test(String(e.exercise_name || ''))) || list[0];
  if (!pick) return null;

  cachedToursCatalog = {
    id: pick.id,
    exercise_name: pick.exercise_name,
    sets: pick.sets,
    reps: pick.reps,
    exercise_type_id: pick.exercise_type_id,
    description: pick.description,
    display_url: pick.display_url,
    display_images: pick.display_images,
    catalog_exercise_type_name: pick.exercise_type_name || 'Tours',
  };
  return cachedToursCatalog;
}

export function buildSyntheticToursExerciseRow(catalog, toursCount, trainingSessionId) {
  return {
    id: null,
    training_session_id: trainingSessionId,
    exercise_id: catalog.id,
    exercise_name: catalog.exercise_name,
    exercise_type: catalog.catalog_exercise_type_name || 'Tours',
    exercise_order: 0,
    sets: catalog.sets ?? 1,
    reps: toursCount,
    weight_kg: null,
    height: null,
    tempo: null,
    description: catalog.description,
    display_url: catalog.display_url,
    display_images: catalog.display_images,
    catalog_exercise_type_id: catalog.exercise_type_id,
    catalog_exercise_type_name: catalog.catalog_exercise_type_name || 'Tours',
    from_session_notes: true,
  };
}

/** Fill Tours from session notes when the API returns no exercise rows. */
export async function hydrateExercisesFromSessionNotes(rows, sessionNotes, trainingSessionId) {
  const list = Array.isArray(rows) ? [...rows] : [];
  if (rowsIncludeToursExercise(list)) return list;
  const toursCount = parseToursCountFromSessionNotes(sessionNotes);
  if (toursCount == null) return list;
  const catalog = await fetchToursCatalogExercise();
  if (!catalog) return list;
  list.push(buildSyntheticToursExerciseRow(catalog, toursCount, trainingSessionId));
  return list;
}
